import i18n, { type AppLanguageCode, type TKey, translateApp } from '@repo/i18n'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
import { z } from 'zod'

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

/** Backend error bodies are thrown directly by the SDK; `code` is top-level. */
export function getErrorCode(error: unknown) {
	return typeof error === 'object' &&
		error !== null &&
		'code' in error &&
		typeof error.code === 'string'
		? error.code
		: undefined
}

export function formatError(error: unknown, language?: AppLanguageCode) {
	const code = getErrorCode(error)
	const key = `shared:http_errors.${code?.toLowerCase()}` as TKey
	const resolvedKey =
		code && i18n.exists(key) ? key : ('shared:http_errors.default' as TKey)

	return language ? translateApp(language, resolvedKey) : i18n.t(resolvedKey)
}

export function translateValidationIssue<Issue extends { code: string }>(
	issue: Issue,
	messages: Partial<Record<string, string | ((issue: Issue) => string)>>,
	fallback: string | ((issue: Issue) => string)
) {
	const message = messages[issue.code] ?? fallback

	if (typeof message === 'function') {
		return message(issue)
	}

	return message
}

/**
 * Function that throws an error, it's useful to prevent impossible states in the application.
 * Example: const id = params.id ?? raise('No id provided'), in this case id is a string instead of string | undefined
 * @param error
 */
export const raise = (error: string): never => {
	throw new Error(error)
}

export function debounce<Args extends unknown[]>(
	callback: (...args: Args) => unknown,
	delay: number
) {
	let timeout: Parameters<typeof clearTimeout>[0] | undefined

	return (...args: Args) => {
		if (timeout !== undefined) {
			clearTimeout(timeout)
		}
		timeout = setTimeout(() => {
			callback(...args)
		}, delay)
	}
}

export function getInitial(value: string) {
	return value.trim().charAt(0).toUpperCase() || '?'
}

export function getInitials(value: string) {
	return value
		.split(' ')
		.map((part) => part[0] ?? '')
		.join('')
		.slice(0, 2)
		.toUpperCase()
}

/** Formats a millisecond duration as `m:ss` (clamped at zero); used by media players and recorders. */
export function formatMediaDuration(durationMs: number) {
	const total = Math.max(0, Math.round(durationMs / 1000))
	return `${Math.floor(total / 60)}:${(total % 60).toString().padStart(2, '0')}`
}

export function formatBytes(bytes: number) {
	if (bytes < 1024) {
		return `${bytes} B`
	}
	const kb = bytes / 1024
	if (kb < 1024) {
		return `${kb.toFixed(0)} KB`
	}
	const mb = kb / 1024
	if (mb < 1024) {
		return `${mb.toFixed(1)} MB`
	}
	const gb = mb / 1024
	if (gb < 1024) {
		return `${gb.toFixed(1)} GB`
	}
	return `${(gb / 1024).toFixed(1)} TB`
}

/**
 * What a control that is currently in force looks like: tinted rather than
 * outlined, so a row of them says which ones are doing something at a glance.
 *
 * Shared because it is not the filters': the inline filter triggers wear it
 * while they are narrowing, and the listing's order control wears it while the
 * order is not the default one. Two copies would be two tints that agree until
 * one of them is edited.
 *
 * Built from `primary` and never from a colour — the brand arrives as a token.
 * It restates `hover` and `aria-expanded` because the `outline` variant owns
 * both: left alone, the tint would drop the moment the pointer crossed the
 * button and stay dropped while the panel was open, which is exactly when
 * somebody is looking at it.
 */
export const controlInForceClass =
	'border-primary/30 bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary aria-expanded:bg-primary/10 aria-expanded:text-primary'

/** Max items of a generated array schema, or null if unconstrained. */
export function zodArrayMaxLength(schema: z.ZodType) {
	return zodMaxConstraint(schema, 'maxItems')
}

/** Max value of a generated number schema, or null if unconstrained. */
export function zodNumberMaxValue(schema: z.ZodType) {
	return zodMaxConstraint(schema, 'maximum')
}

/** Max length of a generated string schema, or null if unconstrained. */
export function zodStringMaxLength(schema: z.ZodType) {
	return zodMaxConstraint(schema, 'maxLength')
}

/** Min length of a generated string schema, or null if unconstrained. */
export function zodStringMinLength(schema: z.ZodType) {
	return zodMaxConstraint(schema, 'minLength')
}

/**
 * Reads a constraint through any optional/default/nullable wrapping via the
 * public API — zod internals (`_zod`) change between releases and generated
 * wrapper nesting changes between SDK releases.
 */
function zodMaxConstraint(
	schema: z.ZodType,
	key: 'maxItems' | 'maximum' | 'maxLength' | 'minLength'
): number | null {
	let current: z.core.$ZodType = schema
	// Wrapper types only — a general unwrap() loop would also unwrap arrays
	// into their element schema, losing the array's own constraints.
	while (
		current instanceof z.ZodOptional ||
		current instanceof z.ZodDefault ||
		current instanceof z.ZodNullable
	) {
		current = current.unwrap()
	}
	// The generator writes a nullable field as `union([T, null])`, not
	// `.nullable()`: the constraint lives on whichever member carries it.
	if (current instanceof z.ZodUnion) {
		for (const option of current.options) {
			const found = zodMaxConstraint(option as z.ZodType, key)
			if (found !== null) {
				return found
			}
		}
		return null
	}
	const value = z.toJSONSchema(current)[key]
	return typeof value === 'number' ? value : null
}
